import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProfileHeader from "../components/profile/ProfileHeader";
import StatsOverview from "../components/profile/StatsOverview";
import ProfileNav from "../components/profile/ProfileNav";
import ContentGrid from "../components/profile/ContentGrid";
import EventList from "../components/profile/EventList";
import CreatedEffects from "../components/profile/CreatedEffects";
import { Event } from "../types/events";
import { Effect } from "../types/effects";
import { RootState, AppDispatch } from "../store/store";
import dreamWorldProvider from "../DreamWorldProvider";
import { UserData } from "../store/dreamworldSlice";
import { LibraryItem } from "../types/library";
import {
  getProfileContent,
  resetProfileContent,
  selectProfileContent,
  selectProfileHasMore,
  selectProfileLastVisible,
  selectProfileLoading,
} from "../store/profileSlice";
import ContentModal from "../components/ContentModal";
import { AnimatePresence } from "framer-motion";
import { toggleLikeContent } from "../store/dreamworldSlice";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import FollowersModal from "../components/profile/FollowersModal";
import { DocumentSnapshot } from "firebase/firestore";
import EffectDescriptionPanel from "../components/create/EffectDescriptionPanel";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/ui/PageLayout";

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const [user, setUser] = useState<UserData | null>(null);
  const [activeTab, setActiveTab] = useState("Publications");
  const [events, setEvents] = useState<Event[]>([]);
  const [likedCreations, setLikedCreations] = useState<LibraryItem[]>([]);
  const [likedLastVisible, setLikedLastVisible] = useState<any>(null);
  const [likedHasMore, setLikedHasMore] = useState(true);
  const [likedLoading, setLikedLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState<LibraryItem | null>(
    null
  );
  const [selectedEffect, setSelectedEffect] = useState<Effect | null>(null);
  const [isFollowersModalOpen, setIsFollowersModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalUsers, setModalUsers] = useState<UserData[]>([]);
  const [followersLastVisible, setFollowersLastVisible] = useState<DocumentSnapshot | null>(null);
  const [followersHasMore, setFollowersHasMore] = useState(true);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingPage, setFollowingPage] = useState(1);
  const [followingTotalPages, setFollowingTotalPages] = useState(1);
  const [followingLoading, setFollowingLoading] = useState(false);

  const currentUser = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );

  const userId = paramUserId ?? currentUser?.userData?.uid;

  const content = useSelector(selectProfileContent);
  const hasMore = useSelector(selectProfileHasMore);
  const loading = useSelector(selectProfileLoading);
  const lastVisible = useSelector(selectProfileLastVisible);

  const loadMoreContent = useCallback(async () => {
    if (loading === "pending" || !hasMore || !userId) return;
    dispatch(
      getProfileContent({ userId, lastVisible: lastVisible || undefined })
    );
  }, [dispatch, hasMore, lastVisible, loading, userId]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (userId) {
        if (userId === currentUser?.userData?.uid && currentUser?.userData) {
          setUser(currentUser.userData);
        } else {
          const userData = await dreamWorldProvider.getuserData(userId);
          setUser(userData);
        }
      }
    };
    fetchUserData();

    // Reset content when userId changes
    dispatch(resetProfileContent());
  }, [userId, dispatch, currentUser]);

  useEffect(() => {
    if (activeTab === "Publications" && userId) {
      loadMoreContent();
    }
  }, [activeTab, userId, loadMoreContent]);

  const loadMoreLikedContent = useCallback(async () => {
    if (likedLoading || !likedHasMore || !userId) return;
    setLikedLoading(true);
    const { items, lastVisible } = await dreamWorldProvider.getLikedContent(
      userId,
      likedLastVisible
    );
    setLikedCreations((prev) => [...prev, ...items]);
    setLikedLastVisible(lastVisible);
    setLikedHasMore(lastVisible !== null);
    setLikedLoading(false);
  }, [userId, likedLoading, likedHasMore, likedLastVisible]);

  useEffect(() => {
    if (activeTab === "Publications" && userId) {
      loadMoreContent();
    } else if (activeTab === "Liked" && userId) {
      loadMoreLikedContent();
    }
  }, [activeTab, userId, loadMoreContent, loadMoreLikedContent]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (userId) {
        const eventsList = await dreamWorldProvider.getUserEvents(userId);
        setEvents(eventsList);
      }
    };

    if (activeTab === "Events" && userId) {
      fetchEvents();
    }
  }, [activeTab, userId]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 1000
      ) {
        if (activeTab === "Publications" && hasMore && loading !== "pending") {
          loadMoreContent();
        } else if (activeTab === "Liked" && likedHasMore && !likedLoading) {
          loadMoreLikedContent();
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    loadMoreContent,
    hasMore,
    loading,
    activeTab,
    likedHasMore,
    likedLoading,
    loadMoreLikedContent,
  ]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingIndicator />
      </div>
    );
  }

  const isOwner = currentUser?.user.uid === userId;

  const handleContentClick = (item: LibraryItem) => {
    setSelectedContent(item);
  };

  const handleEffectClick = (effect: Effect) => {
    setSelectedEffect(effect);
  };

  const handleStartEditing = (
    effect: Effect,
    images: File[],
    userInput?: string
  ) => {
    navigate("/create", {
      state: {
        effect,
        images,
        userInput,
        activeGenerator: effect.type,
      },
    });
  };

  const handleLike = (id: string) => {
    if (currentUser) {
      dispatch(
        toggleLikeContent({ contentId: id, userId: currentUser.user.uid })
      );
    }
  };

  const loadMoreFollowers = async () => {
    if (followersLoading || !followersHasMore || !userId) return;
    setFollowersLoading(true);
    const { followers, lastVisible } = await dreamWorldProvider.getFollowers(
      userId,
      followersLastVisible
    );
    setModalUsers((prev) => [...prev, ...followers]);
    setFollowersLastVisible(lastVisible);
    setFollowersHasMore(lastVisible !== null);
    setFollowersLoading(false);
  };

  const handleFollowersClick = async () => {
    if (userId) {
      setFollowersLoading(true);
      const { followers, lastVisible } = await dreamWorldProvider.getFollowers(userId);
      setModalUsers(followers);
      setFollowersLastVisible(lastVisible);
      setFollowersHasMore(lastVisible !== null);
      setModalTitle("Followers");
      setIsFollowersModalOpen(true);
      setFollowersLoading(false);
    }
  };

  const loadMoreFollowing = async () => {
    if (followingLoading || followingPage >= followingTotalPages || !userId) return;
    setFollowingLoading(true);
    const nextPage = followingPage + 1;
    const { following } = await dreamWorldProvider.getFollowing(userId, nextPage);
    setModalUsers((prev) => [...prev, ...following]);
    setFollowingPage(nextPage);
    setFollowingLoading(false);
  };

  const handleFollowingClick = async () => {
    if (userId) {
      setFollowingLoading(true);
      const { following, totalPages } = await dreamWorldProvider.getFollowing(userId);
      setModalUsers(following);
      setFollowingTotalPages(totalPages);
      setFollowingPage(1);
      setModalTitle("Following");
      setIsFollowersModalOpen(true);
      setFollowingLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsFollowersModalOpen(false);
    setModalUsers([]);
    setFollowersLastVisible(null);
    setFollowersHasMore(true);
    setFollowingPage(1);
    setFollowingTotalPages(1);
  };

  return (
    <>
      <div className="bg-gray-900 min-h-screen text-white">
        <ProfileHeader user={user} isOwner={isOwner} />
        <StatsOverview
          user={user}
          onFollowersClick={handleFollowersClick}
          onFollowingClick={handleFollowingClick}
        />
        <ProfileNav activeTab={activeTab} setActiveTab={setActiveTab} />
        <PageLayout>
          {activeTab === "Publications" && (
            <ContentGrid items={content} onContentClick={handleContentClick} />
          )}
          {activeTab === "Liked" && (
            <ContentGrid
              items={likedCreations}
              onContentClick={handleContentClick}
            />
          )}
          {activeTab === "Events" && <EventList events={events} />}
          {activeTab === "Effects" && (
            <CreatedEffects onEffectClick={handleEffectClick} />
          )}
          {(loading === "pending" || likedLoading) && (
            <div className="flex items-center justify-center py-8">
              <LoadingIndicator />
            </div>
          )}
        </PageLayout>
      </div>
      <AnimatePresence>
        {selectedContent && (
          <ContentModal
            contentId={selectedContent.id}
            source="profile"
            onClose={() => setSelectedContent(null)}
            onLike={handleLike}
          />
        )}
      </AnimatePresence>
      <EffectDescriptionPanel
        effect={selectedEffect}
        onClose={() => setSelectedEffect(null)}
        onStartEditing={handleStartEditing}
      />
      {isFollowersModalOpen && (
        <FollowersModal
          users={modalUsers}
          title={modalTitle}
          onClose={handleCloseModal}
          onLoadMore={modalTitle === 'Followers' ? loadMoreFollowers : loadMoreFollowing}
          hasMore={modalTitle === 'Followers' ? followersHasMore : followingPage < followingTotalPages}
          loading={modalTitle === 'Followers' ? followersLoading : followingLoading}
        />
      )}
    </>
  );
};

export default ProfilePage;
